import React, { useState, useMemo, useCallback } from "react";
import _ from "lodash";
import moment from "moment";
import {
  ChevronLeftOutlined,
  ChevronRightOutlined,
} from "@toddle-edu/ds-icons";
import { IconButton } from "@toddle-edu/ds-web";

// * Internal
import { useI18n } from "Hooks";
import { checkIsDateBefore } from "Utils/DateTimeHelpers";
import { getStyleStrippedText } from "Utils/htmlSanitize";

// * Relative
import {
  getMomentFromDateAndTimeString,
  getOpensOnBasedOnTimelineSettings,
  getDueOnBasedOnTimelineSettings,
  getClosesOnBasedOnTimelineSettings,
} from "../../Utils";
import { getWorksheetAssignSummaryText } from "./WorksheetAssignSummaryUtil";
import classes from "./WorksheetAssignSummary.scss";

const WorksheetAssignSummary = ({
  courses,
  isFixedStartTime,
  isTimedTestEnabled,
  currentTimelineSettings,
  assignmentData,
  timedTestDuration,
}) => {
  const { t } = useI18n(["worksheet"]);
  const [currentClassIndex, setCurrentClassIndex] = useState(0);
  const currentCourse = courses[currentClassIndex];

  const summaryText = useMemo(() => {
    if (!currentCourse) return "";
    const {
      deadlineDate,
      deadlineTime,
      closeSubmissionDate,
      state = {},
      visibility,
    } = currentCourse;
    const { scheduleDate, scheduleTime } = state;
    const oldCourses = _.get(assignmentData, "courses", []);
    const oldCourseData = _.find(oldCourses, { id: currentCourse.id }) || {};
    const {
      state: {
        scheduleDate: oldScheduleDate,
        scheduleTime: oldScheduleTime,
      } = {},
    } = oldCourseData;

    const opensOn = getMomentFromDateAndTimeString({
      dateString: scheduleDate,
      timeString: scheduleTime,
    });
    const dueOn = getMomentFromDateAndTimeString({
      dateString: deadlineDate,
      timeString: deadlineTime,
    });
    const closesOn = getMomentFromDateAndTimeString({
      dateTimeString: closeSubmissionDate,
    });
    const previousOpensOn = getMomentFromDateAndTimeString({
      dateString: oldScheduleDate,
      timeString: oldScheduleTime,
    });

    const dueOnToConsider = getDueOnBasedOnTimelineSettings({
      dueOn,
      currentTimelineSettings,
    });
    const isDueDateInPast = checkIsDateBefore({
      date1: dueOnToConsider,
      date2: moment(),
      key: "minutes",
    });
    const opensOnToConsider = getOpensOnBasedOnTimelineSettings({
      opensOn,
      previousOpensOn,
      currentTimelineSettings,
      isDueDateInPast,
    });
    const closesOnToConsider = getClosesOnBasedOnTimelineSettings({
      closesOn,
      currentTimelineSettings,
    });

    const effectiveDuration =
      currentCourse.timedTestDuration || timedTestDuration;

    return getWorksheetAssignSummaryText({
      isTimedTestEnabled,
      isFixedStartTime,
      opensOn: opensOnToConsider,
      dueOn: dueOnToConsider,
      closesOn: closesOnToConsider,
      visibility,
      timedTestDuration: effectiveDuration,
      t,
    });
  }, [
    t,
    currentCourse,
    isFixedStartTime,
    isTimedTestEnabled,
    timedTestDuration,
    currentTimelineSettings,
    assignmentData,
  ]);

  const handlePreviousClass = useCallback(() => {
    setCurrentClassIndex(prev => (prev > 0 ? prev - 1 : prev));
  }, []);

  const handleNextClass = useCallback(() => {
    setCurrentClassIndex(prev =>
      prev < _.size(courses) - 1 ? prev + 1 : prev
    );
  }, [courses]);

  const showNavigation = _.size(courses) > 1;
  const isPrevDisabled = currentClassIndex === 0;
  const isNextDisabled = currentClassIndex === _.size(courses) - 1;

  if (_.isEmpty(courses)) return null;

  const sanitizedSummary = getStyleStrippedText({
    text: summaryText
      .replace(/<strong>/g, "<span style='color:#222;font-weight:600;'>")
      .replace(/<\/strong>/g, "</span>"),
    type: "SANITIZE_DANGER_HTML_PARAGRAPH",
  });

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <span className="text-label">
          {t("worksheet:worksheet_assign_summary")}
        </span>
        <div className={classes.divider} />
        <div className={classes.classNavigation}>
          <span className="text-label">{currentCourse?.title}</span>
          {showNavigation ? (
            <div className={classes.navigationButtons}>
              <IconButton
                dsVersion="2.0"
                size="small"
                icon={<ChevronLeftOutlined />}
                variant="neutral"
                type="plain"
                onClick={handlePreviousClass}
                disabled={isPrevDisabled}
                testId="worksheetAssignSummary-prev"
                aria-label={t("common:previous")}
              />
              <IconButton
                dsVersion="2.0"
                size="small"
                icon={<ChevronRightOutlined />}
                variant="neutral"
                type="plain"
                onClick={handleNextClass}
                disabled={isNextDisabled}
                testId="worksheetAssignSummary-next"
                aria-label={t("common:next")}
              />
            </div>
          ) : null}
        </div>
      </div>
      <div
        className={classes.summaryText}
        dangerouslySetInnerHTML={{ __html: sanitizedSummary }}
      />
    </div>
  );
};

export default WorksheetAssignSummary;
